import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createStripeSession } from "@/lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { state, dispatch, subtotal, count } = useCart();
  const [step, setStep] = useState("cart"); // cart | details | done
  const [form, setForm] = useState({ email: "", name: "" });
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);

  const close = () => {
    dispatch({ type: "close" });
    setTimeout(() => setStep("cart"), 400);
  };

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await createStripeSession({
        email: form.email,
        name: form.name,
        items: state.items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      if (!res?.url) throw new Error("No checkout URL returned");
      // Redirect to Stripe-hosted checkout
      window.location.href = res.url;
    } catch (err) {
      toast.error("Couldn't start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {state.open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="cart-overlay"
            className="fixed inset-0 z-[60] bg-ink-950/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[70] h-full w-full sm:w-[460px] bg-ink-950 border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold">Your Tsunami</div>
                <div className="font-display font-black text-xl">
                  {step === "done" ? "Confirmed" : step === "details" ? "Details" : `Cart (${count})`}
                </div>
              </div>
              <button
                onClick={close}
                data-testid="cart-close"
                className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-cyan-glow hover:text-cyan-glow"
              >
                <X size={16} />
              </button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-6 space-y-5">
                  {state.items.length === 0 && (
                    <div className="text-center py-16">
                      <div className="font-display font-black text-2xl text-shimmer-gold mb-3">EMPTY</div>
                      <p className="text-sm text-slate-400 font-light">The tsunami hasn't formed yet. Add some merch.</p>
                    </div>
                  )}
                  {state.items.map((i) => (
                    <div
                      key={i.product.id}
                      data-testid={`cart-item-${i.product.id}`}
                      className="flex gap-4 pb-5 border-b border-white/5"
                    >
                      <img src={i.product.image} alt="" className="w-20 h-20 object-cover bg-ink-800" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm leading-tight truncate">{i.product.name}</div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-glow mt-1">
                          ${i.product.price.toFixed(0)}
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() => dispatch({ type: "qty", id: i.product.id, qty: i.quantity - 1 })}
                            data-testid={`qty-dec-${i.product.id}`}
                            className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-cyan-glow"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="font-mono text-sm w-6 text-center">{i.quantity}</span>
                          <button
                            onClick={() => dispatch({ type: "qty", id: i.product.id, qty: i.quantity + 1 })}
                            data-testid={`qty-inc-${i.product.id}`}
                            className="w-7 h-7 border border-white/15 flex items-center justify-center hover:border-cyan-glow"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            onClick={() => dispatch({ type: "remove", id: i.product.id })}
                            data-testid={`remove-${i.product.id}`}
                            className="ml-auto text-slate-500 hover:text-magenta-glow"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 px-6 py-5">
                  <div className="flex justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.3em] text-slate-400">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-display font-black text-2xl text-shimmer-gold">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <button
                    disabled={state.items.length === 0}
                    onClick={() => setStep("details")}
                    data-testid="cart-checkout"
                    className="w-full bg-gold hover:bg-gold-light disabled:opacity-40 text-ink-950 font-black uppercase tracking-[0.25em] text-sm py-4 shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
                  >
                    Checkout the Tsunami
                  </button>
                </div>
              </>
            )}

            {step === "details" && (
              <form onSubmit={checkout} className="flex-1 flex flex-col px-6 py-6">
                <div className="space-y-5 flex-1">
                  <div>
                    <label className="text-[10px] tracking-[0.3em] uppercase text-slate-400">Full Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="checkout-name"
                      className="mt-2 w-full bg-ink-900 border border-white/10 focus:border-gold px-4 py-3 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.3em] uppercase text-slate-400">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="checkout-email"
                      className="mt-2 w-full bg-ink-900 border border-white/10 focus:border-gold px-4 py-3 outline-none"
                    />
                  </div>
                  <p className="text-xs text-slate-500 font-light">
                    You'll be redirected to Stripe to complete payment securely.
                    Shipping address is collected on the next step.
                  </p>
                </div>
                <div className="border-t border-white/10 pt-5 mt-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-xs uppercase tracking-[0.3em] text-slate-400">Total</span>
                    <span className="font-display font-black text-2xl text-shimmer-gold">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-submit"
                    className="w-full bg-cyan-neon hover:bg-cyan-glow text-ink-950 font-black uppercase tracking-[0.25em] text-sm py-4 disabled:opacity-50"
                  >
                    {loading ? "Redirecting…" : "Pay with Stripe"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("cart")}
                    className="w-full text-xs uppercase tracking-[0.3em] text-slate-500 hover:text-white py-2"
                  >
                    ← Back to Cart
                  </button>
                </div>
              </form>
            )}

            {step === "done" && (
              <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-neon to-magenta-hot mb-6 animate-pulse-glow" />
                <div className="font-display font-black text-3xl text-shimmer-gold mb-3">
                  TSUNAMI RESERVED
                </div>
                <p className="text-slate-300 font-light max-w-xs">
                  Order <span className="font-mono text-cyan-glow">{orderId?.slice(0, 8)}</span> confirmed.
                  A payment link is on its way to your inbox.
                </p>
                <button
                  onClick={close}
                  data-testid="cart-done-close"
                  className="mt-10 bg-gold hover:bg-gold-light text-ink-950 font-black uppercase tracking-[0.25em] text-sm px-8 py-4"
                >
                  Keep Riding
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
